import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors with auto-refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Maintenance lockout from the backend — instantly notify the maintenance hook
    // so the lockout page renders without waiting for the 60s poll.
    if (error.response?.status === 503 && error.response?.data?.error_code === 'MAINTENANCE_ACTIVE') {
      try {
        window.dispatchEvent(new CustomEvent('maintenance-active', {
          detail: error.response.data,
        }));
      } catch { /* SSR safety */ }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const { access_token, refresh_token: newRefresh } = response.data;

        localStorage.setItem('token', access_token);
        if (newRefresh) localStorage.setItem('refresh_token', newRefresh);

        api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        processQueue(null, access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const ordersApi = {
  getAll: (status) => api.get('/orders', { params: { status } }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  getStats: () => api.get('/international/orders/stats/summary'),
};

export const pickupsApi = {
  getAll: () => api.get('/pickups'),
  create: (data) => api.post('/pickups', data),
  updateStatus: (id, status) => api.patch(`/pickups/${id}/status`, { status }),
};

export const manifestsApi = {
  getAll: () => api.get('/pickups/manifests'),
  create: (data) => api.post('/pickups/manifests', data),
};

export const walletApi = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: (page = 1, type) => api.get('/wallet/transactions', { params: { page, type } }),
  getRewards: () => api.get('/wallet/rewards'),
  addFunds: (amount) => api.post('/wallet/add-funds', { amount }),
};

export const trackingApi = {
  track: (awb) => api.get(`/tracking/${awb}`),
};

export const supportApi = {
  getTickets: () => api.get('/support/tickets'),
  createTicket: (data) => api.post('/support/tickets', data),
  getTicketById: (id) => api.get(`/support/tickets/${id}`),
  addReply: (id, data) => api.post(`/support/tickets/${id}/reply`, data),
};

export default api;
